// App shell components: admin sidebar and rep top-nav, plus the page router.
import React from 'react';
import { Icon } from './ui.jsx';
import { Logo } from './Logo.jsx';
import { TenantPicker } from './tenants.jsx';
import { ProfileMenu } from './ProfileMenu.jsx';
import { canAccess } from './auth.jsx';

// ---- Collapsible sidebar item ----
function SidebarNav({ nav, current, onNav }) {
  // Track which parent menus are open. A menu auto-opens when its own id
  // or any of its subs matches `current`, so deep links always show context.
  const [openIds, setOpenIds] = React.useState(() => {
    const init = {};
    nav.forEach(item => {
      if (item.sub && (current === item.id || item.sub.some(s => s.id === current))) init[item.id] = true;
    });
    return init;
  });
  // Whenever `current` changes (user navigates), auto-expand the parent whose child is active.
  React.useEffect(() => {
    setOpenIds(prev => {
      const next = { ...prev };
      nav.forEach(item => {
        if (item.sub && item.sub.some(s => s.id === current)) next[item.id] = true;
      });
      return next;
    });
  }, [current]);

  const toggle = (id) => setOpenIds(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <nav className="flex-1 py-3 px-2 space-y-0.5 text-[13px] overflow-y-auto">
      {nav.map(item => {
        const isActive = current === item.id || (item.sub && item.sub.some(s => s.id === current));
        const open = !!openIds[item.id];
        return (
          <div key={item.id}>
            <button
              data-tutorial={`sidebar-${item.id === 's02' ? 'dashboard' : item.id === 's03' ? 'runs' : item.id === 's11' ? 'customers' : item.id === 's22' ? 'users' : item.id === 's14' ? 'rates' : item.id === 's20' ? 'tenants' : ''}`}
              onClick={() => {
                if (item.sub) {
                  toggle(item.id);
                  // Also navigate to the parent's own page on first open (nice default).
                  if (!open && current !== item.id) onNav(item.id);
                } else {
                  onNav(item.id);
                }
              }}
              className={`w-full flex items-center gap-2.5 h-9 px-3 rounded-md transition-colors
                ${isActive ? 'bg-primary-100 text-primary-900 font-semibold' : 'text-neutral-700 hover:bg-neutral-100'}`}>
              <Icon name={item.icon} size={16}/>
              <span className="flex-1 text-left">{item.label}</span>
              {item.sub && (
                <Icon name="chevron" size={12}
                  className={`text-neutral-400 transition-transform ${open ? 'rotate-90' : ''}`}/>
              )}
            </button>
            {item.sub && open && (
              <div className="ml-7 mt-0.5 mb-1 space-y-0.5 animate-[fadeIn_120ms_ease-out]">
                {item.sub.map(s => (
                  <button key={s.id} onClick={() => onNav(s.id)}
                    className={`w-full flex items-center justify-between h-7 px-3 rounded-md text-[12px] transition-colors
                      ${current === s.id ? 'text-primary-900 font-semibold bg-primary-100' : 'text-neutral-600 hover:bg-neutral-100'}`}>
                    <span>{s.label}</span>
                    {s.badge > 0 && <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">{s.badge}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export function AdminShell({ current, onNav, unassignedCount, tenants, activeTenantId, onSelectTenant, onNewTenant, user, onLogout, onHelp, onTenantUpdated, children }) {
  const activeTenant = tenants?.find(t => t.id === activeTenantId) || tenants?.[0];
  // Permission-aware sidebar: each item / sub-item is filtered via
  // canAccess(user, screen). Approver and other restricted roles end up
  // with a smaller nav (no 'New run', no 'Tenants', no 'Roles & permissions')
  // automatically — same canAccess used by URL routing in App.jsx.
  const rawNav = [
    { id: 's02', label: 'Dashboard',       icon: 'dashboard' },
    { id: 's03', label: 'Commission Runs', icon: 'runs', sub: [{ id: 's04', label: 'New run' }] },
    { id: 's11', label: 'Customers',       icon: 'customers', sub: [{ id: 's11u', label: 'Unassigned', badge: unassignedCount }] },
    { id: 's22', label: 'Users and Roles',  icon: 'reps', sub: [
      { id: 's13', label: 'Users' },
      { id: 's26', label: 'Roles & permissions' },
    ] },
    { id: 's23', label: 'Commission plans', icon: 'rates' },
    { id: 's20', label: 'Tenants',         icon: 'settings' },
  ];
  const nav = rawNav
    .filter(n => canAccess(user, n.id))
    .map(n => n.sub
      ? { ...n, sub: n.sub.filter(s => canAccess(user, s.id)) }
      : n);
  const canSeeAudit = canAccess(user, 's19');
  return (
    <div className="min-h-full flex bg-canvas">
      {/* Sidebar */}
      <aside className="w-[240px] shrink-0 bg-white border-r border-neutral-200 flex flex-col">
        <div className="h-[56px] flex items-center gap-2.5 px-5 border-b border-neutral-200">
          <Logo size={28} src={activeTenant?.logoUrl}/>
          <div className="text-[14px] font-semibold text-neutral-900 tracking-[-0.01em] leading-tight truncate">
            {activeTenant?.name || 'Commissions'}
          </div>
        </div>
        <SidebarNav nav={nav} current={current} onNav={onNav}/>
        {canSeeAudit && (
          <div className="p-2 border-t border-neutral-200">
            <button onClick={() => onNav('s19')}
              className={`w-full flex items-center gap-2.5 h-9 px-3 rounded-md text-[13px] ${current === 's19' ? 'bg-primary-100 text-primary-900 font-semibold' : 'text-neutral-600 hover:bg-neutral-100'}`}>
              <Icon name="audit" size={16}/><span>Audit log</span>
            </button>
          </div>
        )}
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-[56px] shrink-0 bg-white border-b border-neutral-200 flex items-center justify-between px-7">
          <div className="text-[12px] text-neutral-500 flex items-center gap-1.5">
            <span>{activeTenant?.name || 'Commissions'}</span>
            <span className="text-neutral-300">·</span>
            <span className="text-neutral-800 font-medium capitalize">{user?.role || 'Admin'}</span>
          </div>
          <div className="flex items-center gap-3">
            {tenants && <div data-tutorial="tenant-picker"><TenantPicker tenants={tenants} activeId={activeTenantId} onSelect={onSelectTenant} onNew={onNewTenant}/></div>}
            <div className="h-5 w-px bg-neutral-200"/>
            <button onClick={onHelp} className="text-[12px] text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1.5">
              <Icon name="info" size={13}/> Help
            </button>
            <div className="h-5 w-px bg-neutral-200"/>
            <ProfileMenu user={user} tenant={activeTenant} onLogout={onLogout} onTenantUpdated={onTenantUpdated}/>
          </div>
        </header>
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-[1320px] mx-auto px-8 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export function RepShell({ current, onNav, repName, user, tenant, onLogout, children }) {
  const tabs = [
    { id: 's15', label: 'This Month',  icon: 'thisMonth' },
    { id: 's16', label: 'By Period',   icon: 'calendar' },
    { id: 's17', label: 'History',     icon: 'history' },
    { id: 's18', label: 'Unpaid',      icon: 'unpaid' },
  ];
  return (
    <div className="min-h-full bg-canvas">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-[1100px] mx-auto h-[56px] px-6 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={28} src={tenant?.logoUrl}/>
            <div className="text-[14px] font-semibold text-neutral-900 tracking-[-0.01em] leading-tight truncate">
              {tenant?.name || 'Commissions'}
            </div>
          </div>
          <div className="flex items-center gap-2 text-[13px]">
            <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-900 flex items-center justify-center text-[11px] font-bold">{repName.split(' ').map(s=>s[0]).join('')}</div>
            <div className="leading-tight">
              <div className="font-semibold text-neutral-900">{repName}</div>
              <div className="text-[11px] text-neutral-500">Sales Rep</div>
            </div>
            {onLogout && <button onClick={onLogout} title="Sign out" className="ml-1 text-neutral-400 hover:text-neutral-700"><Icon name="unlock" size={14}/></button>}
          </div>
        </div>
        <div className="max-w-[1100px] mx-auto px-6 flex gap-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => onNav(t.id)}
              className={`h-[42px] px-3 text-[13px] font-medium inline-flex items-center gap-1.5 border-b-2 -mb-px transition-colors
                ${current === t.id ? 'border-primary-700 text-primary-900' : 'border-transparent text-neutral-600 hover:text-neutral-900'}`}>
              <Icon name={t.icon} size={14}/>{t.label}
            </button>
          ))}
        </div>
      </header>
      <main>
        <div className="max-w-[1100px] mx-auto px-6 py-8">
          {children}
        </div>
      </main>
    </div>
  );
}

